import { useQuery } from "@tanstack/react-query";
import { isMock, getJson } from "./client";

export interface SeasoningPoint {
  mob: number;
  predicted: number;
  actual: number | null;
  nLoans: number;
  nDefaults: number;
  nDefaultsPredicted: number;
}

export interface MonthlyCalibrationPoint {
  month: string;
  mob: number;
  predicted: number;
  actual: number;
}

export interface VintageSeasoning {
  vintage: string;
  maturityPct: number;
  predictedLifetime: number;
  actualToDate: number;
  judgeable: boolean;
  estimatedReadyDate?: string;
  curve: SeasoningPoint[];
  auc: number | null;
  ks: number | null;
  brier: number | null;
  metricTrend: { mob: number; auc: number | null; ks: number | null; brier: number | null }[];
  monthlyPoints: MonthlyCalibrationPoint[];
}

export interface PortfolioRatePoint {
  mob: number;
  predicted: number | null;
  actual: number | null;
}

export interface SeasoningResponse {
  vintages: VintageSeasoning[];
  portfolioRateCurve: PortfolioRatePoint[];
}

export const VINTAGE_COLORS: Record<string, string> = {
  "2018": "#22d3ee",
  "2019": "#60a5fa",
  "2020": "#a78bfa",
  "2021": "#f472b6",
  "2022": "#fb923c",
  "2023": "#facc15",
  "2024": "#94a3b8",
};

export const METRIC_BANDS = {
  AUC:   { min: 0.65, betterIsHigher: true,  greenAbove: true,  domain: [0.5, 0.8]  as [number, number] },
  KS:    { min: 0.25, betterIsHigher: true,  greenAbove: true,  domain: [0.0, 0.5]  as [number, number] },
  Brier: { max: 0.15, betterIsHigher: false, greenAbove: false, domain: [0.0, 0.25] as [number, number] },
};

const MOB_GRID = [0, 6, 12, 18, 24, 30, 36, 42, 48];

function share(mob: number): number {
  const x = (mob - 16) / 7;
  const s = 1 / (1 + Math.exp(-x));
  const s0 = 1 / (1 + Math.exp(-(-16) / 7));
  const s48 = 1 / (1 + Math.exp(-(48 - 16) / 7));
  return (s - s0) / (s48 - s0);
}

function buildCurve(
  predictedLifetime: number,
  actualToDate: number,
  maturityPct: number,
  nLoansBase = 4200,
): SeasoningPoint[] {
  const maxMob = Math.round((maturityPct / 100) * 48);
  return MOB_GRID.map((mob) => {
    const pShare = share(mob);
    const predicted = +(predictedLifetime * pShare).toFixed(3);
    let actual: number | null;
    if (mob === 0) {
      actual = 0;
    } else if (mob <= maxMob) {
      const aScale = share(maxMob);
      const aShare = aScale === 0 ? 0 : share(mob) / aScale;
      actual = +(actualToDate * aShare).toFixed(3);
    } else {
      actual = null;
    }
    return {
      mob,
      predicted,
      actual,
      nLoans: nLoansBase,
      nDefaults: Math.round(((actual ?? 0) / 100) * nLoansBase),
      nDefaultsPredicted: Math.round((predicted / 100) * nLoansBase),
    };
  });
}

function buildMetricTrend(
  finalAuc: number | null,
  finalKs: number | null,
  finalBrier: number | null,
  maturityPct: number,
) {
  const maxMob = Math.round((maturityPct / 100) * 48);
  return MOB_GRID.map((mob) => {
    if (finalAuc === null || mob < 12 || mob > maxMob) {
      return { mob, auc: null, ks: null, brier: null };
    }
    const t = Math.min(1, (mob - 12) / Math.max(6, maxMob - 12));
    const auc = +(finalAuc - (1 - t) * 0.08).toFixed(3);
    const ks = finalKs === null ? null : +(finalKs - (1 - t) * 0.07).toFixed(3);
    const brier =
      finalBrier === null ? null : +(finalBrier + (1 - t) * 0.012).toFixed(3);
    return { mob, auc, ks, brier };
  });
}

function seededRand(seed: number) {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildMonthlyPoints(
  vintage: string,
  predicted: number,
  actual: number,
): MonthlyCalibrationPoint[] {
  const rand = seededRand(parseInt(vintage, 10) || 1);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const n = 10;
  const pSpread = Math.max(0.25, predicted * 0.18);
  const aSpread = Math.max(0.4, actual * 0.22);
  const now = new Date();
  const nowMonths = now.getFullYear() * 12 + now.getMonth();
  const vintageYear = parseInt(vintage, 10) || now.getFullYear();
  return Array.from({ length: n }, (_, i) => {
    const px = +(predicted + (rand() - 0.5) * 2 * pSpread).toFixed(2);
    const py = +(actual + (rand() - 0.5) * 2 * aSpread).toFixed(2);
    // Months on book for this booking-month cohort as of today.
    const mob = Math.max(0, nowMonths - (vintageYear * 12 + i));
    return {
      month: months[i % 12],
      mob,
      predicted: Math.max(0, px),
      actual: Math.max(0, py),
    };
  });
}

interface Anchor {
  vintage: string;
  predicted: number;
  actual: number;
  maturityPct: number;
  auc: number | null;
  ks: number | null;
  brier: number | null;
  estimatedReadyDate?: string;
}

const ANCHORS: Anchor[] = [
  { vintage: "2018", predicted: 4.0, actual: 5.51, maturityPct: 100, auc: 0.71, ks: 0.34, brier: 0.041 },
  { vintage: "2019", predicted: 3.73, actual: 4.78, maturityPct: 100, auc: 0.7, ks: 0.33, brier: 0.039 },
  { vintage: "2020", predicted: 3.07, actual: 4.18, maturityPct: 95, auc: 0.69, ks: 0.31, brier: 0.037 },
  { vintage: "2021", predicted: 3.64, actual: 6.08, maturityPct: 88, auc: 0.68, ks: 0.3, brier: 0.045 },
  { vintage: "2022", predicted: 2.8, actual: 6.11, maturityPct: 78, auc: 0.67, ks: 0.29, brier: 0.048 },
  { vintage: "2023", predicted: 1.89, actual: 2.63, maturityPct: 60, auc: 0.65, ks: 0.27, brier: 0.026 },
  {
    vintage: "2024",
    predicted: 0.65,
    actual: 0.66,
    maturityPct: 28,
    auc: null,
    ks: null,
    brier: null,
    estimatedReadyDate: "Q4 2026",
  },
];

function buildPortfolioRateCurve(vintages: VintageSeasoning[]): PortfolioRatePoint[] {
  const mobs = [0, 6, 12, 18, 24, 30, 36, 42, 48];
  return mobs.map((mob) => {
    let sumNLoansActual = 0, sumNDefaultsActual = 0;
    let sumNLoansPredicted = 0, sumNDefaultsPredicted = 0;
    for (const v of vintages) {
      const pt = v.curve.find((c) => c.mob === mob);
      if (!pt) continue;
      sumNLoansPredicted += pt.nLoans;
      sumNDefaultsPredicted += (pt.predicted / 100) * pt.nLoans;
      if (pt.actual !== null) {
        sumNLoansActual += pt.nLoans;
        sumNDefaultsActual += pt.nDefaults;
      }
    }
    return {
      mob,
      predicted: sumNLoansPredicted > 0 ? +((sumNDefaultsPredicted / sumNLoansPredicted) * 100).toFixed(3) : null,
      actual: sumNLoansActual > 0 ? +((sumNDefaultsActual / sumNLoansActual) * 100).toFixed(3) : null,
    };
  });
}

const MOCK_VINTAGES: VintageSeasoning[] = ANCHORS.map((a) => ({
  vintage: a.vintage,
  maturityPct: a.maturityPct,
  predictedLifetime: a.predicted,
  actualToDate: a.actual,
  judgeable: a.maturityPct >= 60,
  estimatedReadyDate: a.estimatedReadyDate,
  curve: buildCurve(a.predicted, a.actual, a.maturityPct),
  auc: a.auc,
  ks: a.ks,
  brier: a.brier,
  metricTrend: buildMetricTrend(a.auc, a.ks, a.brier, a.maturityPct),
  // Build monthly cohorts for every vintage; the UI gates them per-cohort by MOB.
  monthlyPoints: buildMonthlyPoints(a.vintage, a.predicted, a.actual),
}));

const MOCK: SeasoningResponse = {
  vintages: MOCK_VINTAGES,
  portfolioRateCurve: buildPortfolioRateCurve(MOCK_VINTAGES),
};

export async function fetchSeasoning(loanType?: string, modelId: number = 1): Promise<SeasoningResponse> {
  if (isMock) return Promise.resolve(MOCK);
  const loanTypeParam = loanType && loanType !== "OVERALL" ? `&loan_type=${loanType}` : "";
  return getJson<SeasoningResponse>(`model-drift-monitoring/seasoning?model_id=${modelId}${loanTypeParam}`);
}

export function useSeasoning(loanType?: string, modelId: number = 1) {
  return useQuery({
    queryKey: ["seasoning", loanType ?? "OVERALL", modelId],
    queryFn: () => fetchSeasoning(loanType, modelId),
  });
}
